import React, { useEffect, useState } from 'react';
import { Button, SimpleGrid, Stack, useToast } from '@chakra-ui/react';
import { ArrowLeft, Save, SendHorizonal } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCatalogueItems } from '../hooks/catalogueItems';
import { getOrgQuote, updateOrgQuote } from '../hooks/org_quotes';
import { createQuoteItem, deleteQuoteItem, updateQuoteItem } from '../hooks/quote_items';
import { Quote, QuoteItem } from '../utils/types';
import {
  QuoteHero,
  QuotePrimaryButton,
  QuoteSectionCard,
  QuoteShell,
} from './quotes/components/quoteUi';
import {
  QuoteLineItemsSection,
  QuoteNotesSection,
  QuoteRecipientSection,
  QuoteSummaryRail,
} from './quotes/components/quoteBuilderShared';

const normalizeProducts = (data: unknown) => {
  if (Array.isArray(data)) {
    return data as Array<{ id: string; name: string; price?: number }>;
  }

  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: Array<{ id: string; name: string; price?: number }> }).results;
  }

  return [];
};

const createDraftItem = (): QuoteItem => ({
  id: Date.now(),
  item_name: '',
  quantity: 1,
  unit_price: 0,
  total_price: 0,
});

export default function EditQuote() {
  const { orgId, quoteId, catalogueId } = useParams<{
    orgId: string;
    quoteId: string;
    catalogueId?: string;
  }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [quote, setQuote] = useState<Quote | null>(null);
  const [products, setProducts] = useState<Array<{ id: string; name: string; price?: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      const [quoteResponse, productResponse] = await Promise.all([
        getOrgQuote(orgId, quoteId),
        getCatalogueItems(orgId || '', catalogueId || ''),
      ]);

      if (!mounted) {
        return;
      }

      if (quoteResponse?.success) {
        setQuote(quoteResponse.data);
      }

      if (productResponse?.success) {
        setProducts(normalizeProducts(productResponse.data));
      }

      setLoading(false);
    };

    load();
    return () => {
      mounted = false;
    };
  }, [orgId, quoteId, catalogueId]);

  const selectedLead = quote?.lead;

  const handleChangeItem = (
    index: number,
    field: keyof QuoteItem,
    value: string | number,
  ) => {
    setQuote((current) => {
      if (!current) {
        return current;
      }

      const nextItems = [...current.items];
      const item = nextItems[index];
      if (!item) {
        return current;
      }

      const updated = { ...item, [field]: value };
      if (field === 'item_name') {
        const selectedProduct = products.find((product) => product.name === value);
        updated.unit_price = Number(selectedProduct?.price || updated.unit_price || 0);
      }

      updated.total_price = Number(updated.quantity || 0) * Number(updated.unit_price || 0);
      nextItems[index] = updated;

      const total = nextItems.reduce(
        (sum, quoteItem) => sum + Number(quoteItem.total_price || 0),
        0,
      );

      return { ...current, items: nextItems, total_amount: total };
    });
  };

  const handleAddItem = async () => {
    if (!orgId || !quoteId || !quote) {
      return;
    }

    const response = await createQuoteItem(orgId, quoteId, 'New item', 1, 0);

    if (!response.success) {
      toast({
        title: 'Could not add line item',
        description: 'Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setQuote((current) =>
      current ? { ...current, items: [...current.items, response.data] } : current,
    );
  };

  const handleDuplicateItem = async (index: number) => {
    if (!orgId || !quoteId || !quote?.items[index]) {
      return;
    }

    const item = quote.items[index];
    const response = await createQuoteItem(
      orgId,
      quoteId,
      item.item_name,
      item.quantity,
      item.unit_price,
    );

    if (response.success) {
      setQuote((current) =>
        current ? { ...current, items: [...current.items, response.data] } : current,
      );
    }
  };

  const handleRemoveItem = async (index: number) => {
    if (!orgId || !quoteId || !quote) {
      return;
    }

    const item = quote.items[index];
    if (!item) {
      return;
    }

    if (item.id) {
      await deleteQuoteItem(orgId, quoteId, item.id);
    }

    setQuote((current) => {
      if (!current) {
        return current;
      }

      const nextItems = current.items.filter((_, itemIndex) => itemIndex !== index);
      const total = nextItems.reduce(
        (sum, quoteItem) => sum + Number(quoteItem.total_price || 0),
        0,
      );
      return { ...current, items: nextItems, total_amount: total };
    });
  };

  const handleSubmit = async () => {
    if (!quote || !orgId || !quoteId) {
      return;
    }

    setSaving(true);
    try {
      await updateOrgQuote(orgId, quoteId, {
        notes: quote.notes,
        status: quote.status,
        total_amount: quote.total_amount,
      });

      for (const item of quote.items) {
        if (item.id) {
          await updateQuoteItem(orgId, quoteId, item.id, {
            item_name: item.item_name,
            quantity: item.quantity,
            unit_price: item.unit_price,
          });
        }
      }

      toast({
        title: 'Quotation updated',
        description: 'All changes were saved successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate(`/organization/${orgId}/quotes/view/${quoteId}`);
    } catch (error) {
      toast({
        title: 'Could not save quotation',
        description: 'Please try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !quote) {
    return (
      <QuoteShell compact>
        <QuoteSectionCard
          title="Loading quotation"
          description="Please wait while the builder loads the quotation and its line items."
        >
          <Button variant="ghost" isLoading loadingText="Loading" />
        </QuoteSectionCard>
      </QuoteShell>
    );
  }

  return (
    <QuoteShell compact>
      <QuoteHero
        eyebrow="Edit quotation"
        title={quote.reference_number}
        description="Revise the pricing, line items, and customer-facing notes without losing visibility into the total."
        primaryAction={
          <QuotePrimaryButton
            leftIcon={<Save size={16} />}
            onClick={handleSubmit}
            isLoading={saving}
            loadingText="Saving"
          >
            Save changes
          </QuotePrimaryButton>
        }
        secondaryAction={
          <Button borderRadius="16px" variant="outline" leftIcon={<ArrowLeft size={16} />} onClick={() => navigate(-1)}>
            Back
          </Button>
        }
      />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
        <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
          <QuoteRecipientSection
            selectedLeadId={String(selectedLead?.id || '')}
            selectedLead={selectedLead}
            selector={null}
          />

          <QuoteLineItemsSection
            items={quote.items}
            products={products}
            onAddItem={handleAddItem}
            onDuplicateItem={handleDuplicateItem}
            onChangeItem={handleChangeItem}
            onRemoveItem={handleRemoveItem}
          />

          <QuoteNotesSection
            notes={quote.notes || ''}
            validUntil={quote.valid_until}
            onChangeNotes={(value) => setQuote((current) => (current ? { ...current, notes: value } : current))}
            onChangeValidUntil={(value) =>
              setQuote((current) => (current ? { ...current, valid_until: value || null } : current))
            }
          />
        </Stack>

        <Stack spacing={6}>
          <QuoteSummaryRail
            referenceNumber={quote.reference_number}
            status={quote.status}
            items={quote.items}
            notes={quote.notes || ''}
            validUntil={quote.valid_until}
          />
          <QuoteSectionCard
            title="Actions"
            description="Keep the main quotation actions stable while you edit."
          >
            <Stack spacing={3}>
              <QuotePrimaryButton
                leftIcon={<Save size={16} />}
                onClick={handleSubmit}
                isLoading={saving}
                loadingText="Saving"
              >
                Save changes
              </QuotePrimaryButton>
              <Button
                borderRadius="16px"
                variant="outline"
                leftIcon={<SendHorizonal size={16} />}
                onClick={() => navigate(`/organization/${orgId}/quotes/view/${quoteId}`)}
              >
                Review quote
              </Button>
            </Stack>
          </QuoteSectionCard>
        </Stack>
      </SimpleGrid>
    </QuoteShell>
  );
}
